<script lang="ts">
	// @ts-nocheck

	import Button from '$lib/components/ui/button/button.svelte';
	import * as Table from '$lib/components/ui/table/';
	import * as Card from '$lib/components/ui/card';
	import * as Carousel from '$lib/components/ui/carousel';
	import {
		Check,
		CheckCircle,
		Loader2,
		ChevronDown,
		Footprints,
		Mail,
		MapPin,
		Phone,
		Star
	} from 'lucide-svelte';
	import { Input } from '$lib/components/ui/input';
	import { Label } from '$lib/components/ui/label';
	import * as Select from '$lib/components/ui/select';
	import { Textarea } from '$lib/components/ui/textarea';
	import * as Alert from '$lib/components/ui/alert';
	import { fly } from 'svelte/transition';
	import { onMount } from 'svelte';
	import Seo from '$lib/components/SEO.svelte';
	import { trackPhoneClick } from '$lib/utils/analytics';
	let animate = false;

	onMount(() => {
		setTimeout(() => {
			window.scrollTo(0, 0);
		}, 200);
		animate = true;
	});

	const contactMethods = [
		{ value: 'phone', label: 'Phone' },
		{ value: 'email', label: 'Email' }
	];

	let submitted = $state(false);
	let loading = $state(false);

	let name = $state('');
	let email = $state('');
	let phone = $state('');
	let preferredContact = $state('Either');
	let message = $state('');
	let type = 'Private Tours Form';

	const triggerContent = $derived(
		contactMethods.find((f) => f.value === preferredContact)?.label ?? 'Either'
	);

	function resetForm() {
		name = '';
		email = '';
		phone = '';
		preferredContact = '';
		message = '';
	}

	async function handleSubmit(e: Event) {
		e.preventDefault();
		loading = true;
		const formData = {
			name,
			email,
			phone,
			preferredContact,
			message,
			type
		};

		try {
			const response = await fetch('/email/send-email', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(formData)
			});

			if (!response.ok) {
				throw new Error('Failed to send email');
			}

			const result = await response.json();

			if (result.success) {
				resetForm();
				submitted = true;
			} else {
				throw new Error(result.error || 'Failed to send email');
			}
		} catch (error) {
			console.error('Error sending email:', error);
			// Handle error (e.g., show error message to user)
		} finally {
			loading = false;
		}
	}

	function handleAnchorClick(event) {
		event.preventDefault();
		const link = event.currentTarget;
		const anchorId = new URL(link.href).hash.replace('#', '');
		const anchor = document.getElementById(anchorId);
		window.scrollTo({
			top: anchor.offsetTop,
			behavior: 'smooth'
		});
	}
</script>

<Seo
	title="Private Tours - Tasmania Local Tour - Tassie Tours"
	description="Get in Touch with Tassie Tours about our range of Fun Tasmania Tour Packages by email or call 0487-163-624."
	keywords="Tasmania Tours, Tasmania local tour, Tassie Tours, Day tours from Hobart, Discover Bruny Island, Cradle Mountain, Active Tasmania tours"
	siteName="Tassie Tours"
	imageURL="/images/hero-carousel-1.jpg"
	logo="/src/lib/assets/logo/logo-black.png"
	author="Tassie Tours"
	type="website"
	name="Tassie Tours"
	index={true}
	twitter={true}
	openGraph={true}
	schemaOrg={true}
	socials={[]}
/>

<section class="relative mx-auto flex h-[600px] flex-col items-center justify-center">
	<enhanced:img
		src="$lib/assets/images/private-tours-1.jpg"
		alt="Tassie Tours Private Tours - Experience Tasmania"
		class="absolute inset-0 h-[600px] w-full object-cover"
	/>

	<div class="absolute inset-0 bg-foreground/50"></div>

	<div
		class="pointer-events-none absolute inset-0 mx-auto flex h-[600px] w-full max-w-7xl flex-col justify-center px-4"
	>
		<h1
			class="mx-auto max-w-2xl scroll-m-20 text-left text-center font-heading text-2xl font-bold tracking-tight text-background lg:mx-0 lg:text-left lg:text-4xl"
		>
			Private Tours
		</h1>
		<div class="mx-auto mt-6 flex max-w-xl items-center lg:mx-0">
			<p class="text-md mx-auto text-center text-background lg:mx-0 lg:text-left lg:text-lg">
				Whether you're an adventurous solo traveller or part of a small to medium-sized group, our
				private tour packages are the ultimate way to discover the wonders of Tasmania. Let us take
				care of all the details while you immerse yourself in the beauty and charm of this
				incredible island.
			</p>
		</div>
		<div class=" mt-6 flex max-w-lg flex-col-reverse items-center gap-4 lg:flex-row">
			<Button
				href="#contact"
				onclick={handleAnchorClick}
				data-sveltekit-noscroll
				class="pointer-events-auto mx-auto w-44 rounded-full  bg-tt-red px-6 text-lg font-semibold text-background hover:bg-tt-red/85  lg:mx-0"
				>Get in touch</Button
			>
			<Button
				href="#destinations"
				onclick={handleAnchorClick}
				variant="outline"
				data-sveltekit-noscroll
				class="pointer-events-auto mx-auto w-44 rounded-full border-2 bg-transparent px-6 text-lg font-semibold text-background  lg:mx-0"
				>Learn more</Button
			>
		</div>
	</div>
</section>

<section class="mx-auto bg-secondary px-4 py-12 lg:py-24">
	<div class=" mx-auto mt-12 max-w-7xl max-w-7xl px-4">
		<div class="mx-auto lg:mx-0">
			<h2 class="font-heading text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
				Experience the Best of Tasmania with our Private Tour Packages
			</h2>

			<p class="leading-7 [&:not(:first-child)]:mt-6">
				Whether you're an adventurous solo traveller or part of a small to medium-sized group, our
				private tour packages are the ultimate way to discover the wonders of Tasmania. Let us take
				care of all the details while you immerse yourself in the beauty and charm of this
				incredible island.
			</p>
			<p class="leading-7 [&:not(:first-child)]:mt-6">
				Our team of local guides are passionate about Tasmania and eager to share their extensive
				knowledge with you. With their expertise and insider tips, you'll gain a deeper
				understanding of Tasmania's rich history, breathtaking landscapes, and unique wildlife.
			</p>
			<p class="leading-7 [&:not(:first-child)]:mt-6">
				We've designed a range of private tour packages to make it easy for you. Choose from our
				carefully crafted itineraries, ranging from 4 to 7 days that can start in Hobart or
				Launceston.
			</p>
			<p class="leading-7 [&:not(:first-child)]:mt-6">
				Each private package is thoughtfully curated to showcase the very best that Tasmania has to
				offer. With a total of 6 packages to choose from, we're confident that you'll find the
				perfect tour itinerary to suit your preferences and interests.
			</p>
			<p class="leading-7 [&:not(:first-child)]:mt-6">
				If there's a specific itinerary or additional location you desire to visit that is not
				included in our list, there's no need to worry. Simply reach out to us, and our dedicated
				team will be delighted to tailor a personalised package that caters to your unique interests
				and desires. You will also find 8 of our top day tour destinations below that can be
				packaged together to make 1 to 6 day itineraries.
			</p>
			<p class="leading-7 [&:not(:first-child)]:mt-6">
				<Button variant="link" class="text-md px-0 font-semibold" href="/tours"
					>Book your private tour today</Button
				> and get ready to create memories that will last a lifetime.
			</p>
		</div>
		<div
			class="mx-auto mt-12 grid grid-cols-1 gap-x-8 gap-y-12 text-base leading-7 sm:grid-cols-2 lg:mx-0 lg:max-w-none"
		></div>
	</div>
</section>

<section class="mx-auto max-w-7xl px-4" id="destinations">
	<div class="py-24">
		<div class="">
			<div class="mx-auto max-w-2xl px-4 py-12 lg:max-w-7xl lg:py-24">
				<div class="grid grid-cols-1 items-center gap-x-8 gap-y-16 lg:grid-cols-2">
					<div>
						<div class="border-b border-gray-200 pb-10">
							<h2 class="font-medium">Private tour package 1</h2>
							<p class="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
								3 Days / 2 Nights
							</p>
							<div class="mt-4 flex items-center text-[#194139]">
								<MapPin class="mr-2 h-4 w-4 " />
								<p>Departs and finishes in Hobart</p>
							</div>
						</div>

						<div class="mt-10">
							<p class="font-semibold leading-7 [&:not(:first-child)]:mt-6">
								This tour includes the following:
							</p>
							<ul class="my-6 [&>li]:mt-2">
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 1:</span> Hobart Highlights
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 2:</span> Bruny Island
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 3:</span> Port Arthur
									</p>
								</li>
							</ul>
							<p class="font-semibold leading-7 [&:not(:first-child)]:mt-6">
								Prices based on the number of passengers
							</p>

							<Table.Root class="mt-6">
								<Table.Header>
									<Table.Row>
										<Table.Head>0-6</Table.Head>
										<Table.Head>7-12</Table.Head>
										<Table.Head>13-18</Table.Head>
										<Table.Head>19-24</Table.Head>
									</Table.Row>
								</Table.Header>
								<Table.Body>
									<Table.Row>
										<Table.Cell>$4,693</Table.Cell>
										<Table.Cell>$5,306</Table.Cell>
										<Table.Cell>$5,919</Table.Cell>
										<Table.Cell>$6,533</Table.Cell>
									</Table.Row>
								</Table.Body>
							</Table.Root>
							<p class="mt-6 text-sm">
								<span class="underline">Please note:</span> Prices listed exclude meals, accommodation,
								entry and parks pass fees.
							</p>
							<Button
								href="/tours/private-tour-package-1-hobart-bruny-island-port-arthur"
								data-sveltekit-noscroll
								class="pointer-events-auto mx-auto mt-6 w-44  rounded-full bg-tt-red px-6 text-lg font-semibold text-background  hover:bg-tt-red/85 lg:mx-0"
								>Book now</Button
							>
						</div>
					</div>

					<div>
						<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
							<enhanced:img
								src="$lib/assets/images/bruny-island-day-tour.jpg"
								alt="Bruny Island"
								class="h-full w-full object-cover object-center"
							/>
						</div>
						<div class="mt-4 grid grid-cols-2 gap-4 sm:mt-6 sm:gap-6 lg:mt-8 lg:gap-8">
							<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
								<enhanced:img
									src="$lib/assets/images/cascade.png"
									alt="Bruny Island"
									class="h-full w-full object-cover object-center"
								/>
							</div>
							<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
								<enhanced:img
									src="$lib/assets/images/port-arthur-1.jpg"
									alt="Bruny Island"
									class="h-full w-full object-cover object-center"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="">
			<div class="mx-auto max-w-2xl px-4 py-12 lg:max-w-7xl lg:py-24">
				<div class="grid grid-cols-1 items-center gap-x-8 gap-y-16 lg:grid-cols-2">
					<div class="lg:order-2">
						<div class="border-b border-gray-200 pb-10">
							<h2 class="font-medium">Private tour package 2</h2>
							<p class="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
								3 Days / 2 Nights
							</p>
							<div class="mt-4 flex items-center text-[#194139]">
								<MapPin class="mr-2 h-4 w-4 " />
								<p>Departs and finishes in Hobart</p>
							</div>
						</div>

						<div class="mt-10">
							<p class="font-semibold leading-7 [&:not(:first-child)]:mt-6">
								This tour includes the following:
							</p>
							<ul class="my-6 [&>li]:mt-2">
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 1:</span> Hobart Highlights
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 1:</span> Bruny Island
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 3:</span> Mt Field National Park and Bonorong Wildlife
										Sanctuary
									</p>
								</li>
							</ul>
							<p class="font-semibold leading-7 [&:not(:first-child)]:mt-6">
								Prices based on the number of passengers
							</p>

							<Table.Root class="mt-6">
								<Table.Header>
									<Table.Row>
										<Table.Head>0-6</Table.Head>
										<Table.Head>7-12</Table.Head>
										<Table.Head>13-18</Table.Head>
										<Table.Head>19-24</Table.Head>
									</Table.Row>
								</Table.Header>
								<Table.Body>
									<Table.Row>
										<Table.Cell>$5,160</Table.Cell>
										<Table.Cell>$5,685</Table.Cell>
										<Table.Cell>$6,210</Table.Cell>
										<Table.Cell>$6,733</Table.Cell>
									</Table.Row>
								</Table.Body>
							</Table.Root>
							<p class="mt-6 text-sm">
								<span class="underline">Please note:</span> Prices listed exclude meals, accommodation,
								entry and parks pass fees.
							</p>
							<Button
								href="/tours/private-tour-package-2-hobart-bruny-island-mt-field-with-bonorong-wildlife-sanctuary"
								data-sveltekit-noscroll
								class="pointer-events-auto mx-auto mt-6 w-44  rounded-full bg-tt-red px-6 text-lg font-semibold text-background  hover:bg-tt-red/85 lg:mx-0"
								>Book now</Button
							>
						</div>
					</div>

					<div>
						<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
							<enhanced:img
								src="$lib/assets/images/bruny.jpg"
								alt="Port Arthur"
								class="h-full w-full object-cover object-center"
							/>
						</div>
						<div class="mt-4 grid grid-cols-2 gap-4 sm:mt-6 sm:gap-6 lg:mt-8 lg:gap-8">
							<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
								<enhanced:img
									src="$lib/assets/images/hobart-highlights.jpg"
									alt="Port Arthur"
									class="h-full w-full object-cover object-center"
								/>
							</div>
							<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
								<enhanced:img
									src="$lib/assets/images/russell-falls.jpg"
									alt="Port Arthur"
									class="h-full w-full object-cover object-center"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="">
			<div class="mx-auto max-w-2xl px-4 py-12 lg:max-w-7xl lg:py-24">
				<div class="grid grid-cols-1 items-center gap-x-8 gap-y-16 lg:grid-cols-2">
					<div class="">
						<div class="border-b border-gray-200 pb-10">
							<h2 class="font-medium">Private tour package 3</h2>
							<p class="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
								4 Days / 3 Nights
							</p>
							<div class="mt-4 flex items-center text-[#194139]">
								<MapPin class="mr-2 h-4 w-4 " />
								<p>Departs from Hobart</p>
							</div>
						</div>

						<div class="mt-10">
							<p class="font-semibold leading-7 [&:not(:first-child)]:mt-6">
								This tour includes the following:
							</p>
							<ul class="my-6 [&>li]:mt-2">
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 1:</span> Hobart City tour and Mt Wellington
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 2:</span> Bruny Island
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 3:</span> Freycinet National Park including Wineglass
										Bay
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 4:</span> Port Arthur and Tasman Sites
									</p>
								</li>
							</ul>
							<p class="font-semibold leading-7 [&:not(:first-child)]:mt-6">
								Prices based on the number of passengers
							</p>

							<Table.Root class="mt-6">
								<Table.Header>
									<Table.Row>
										<Table.Head>0-6</Table.Head>
										<Table.Head>7-12</Table.Head>
										<Table.Head>13-18</Table.Head>
										<Table.Head>19-24</Table.Head>
									</Table.Row>
								</Table.Header>
								<Table.Body>
									<Table.Row>
										<Table.Cell>$6,453</Table.Cell>
										<Table.Cell>$7,235</Table.Cell>
										<Table.Cell>$8,020</Table.Cell>
										<Table.Cell>$8,800</Table.Cell>
									</Table.Row>
								</Table.Body>
							</Table.Root>
							<p class="mt-6 text-sm">
								<span class="underline">Please note:</span> Prices listed exclude meals, accommodation,
								entry and parks pass fees.
							</p>
							<Button
								href="/tours/private-tour-package-3-hobart-bruny-island-freycinet-national-park-port-arthur"
								data-sveltekit-noscroll
								class="pointer-events-auto mx-auto mt-6 w-44  rounded-full bg-tt-red px-6 text-lg font-semibold text-background  hover:bg-tt-red/85 lg:mx-0"
								>Book now</Button
							>
						</div>
					</div>

					<div>
						<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
							<enhanced:img
								src="$lib/assets/images/wineglass.jpg"
								alt="Wineglass Bay"
								class="h-full w-full object-cover object-center"
							/>
						</div>
						<div class="mt-4 grid grid-cols-2 gap-4 sm:mt-6 sm:gap-6 lg:mt-8 lg:gap-8">
							<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
								<enhanced:img
									src="$lib/assets/images/devils-corner.jpg"
									alt="Wineglass Bay"
									class="h-full w-full object-cover object-center"
								/>
							</div>
							<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
								<enhanced:img
									src="$lib/assets/images/bruny-island-3.jpg"
									alt="Wineglass Bay"
									class="h-full w-full object-cover object-center"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="">
			<div class="mx-auto max-w-2xl px-4 py-12 lg:max-w-7xl lg:py-24">
				<div class="grid grid-cols-1 items-center gap-x-8 gap-y-16 lg:grid-cols-2">
					<div class="lg:order-2">
						<div class="border-b border-gray-200 pb-10">
							<h2 class="font-medium">Private tour package 4</h2>
							<p class="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
								4 Days / 3 Nights
							</p>
							<div class="mt-4 flex items-center text-[#194139]">
								<MapPin class="mr-2 h-4 w-4 " />
								<p>Departs from Hobart. Finishes in Launceston. (Can be done in reverse)</p>
							</div>
						</div>

						<div class="mt-10">
							<p class="font-semibold leading-7 [&:not(:first-child)]:mt-6">
								This tour includes the following:
							</p>
							<ul class="my-6 [&>li]:mt-2">
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 1:</span> Bruny Island
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 2:</span> Freycinet National Park including Wineglass
										Bay
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 3:</span> Cradle Mountain
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 4:</span> Launceston
									</p>
								</li>
							</ul>
							<p class="font-semibold leading-7 [&:not(:first-child)]:mt-6">
								Prices based on the number of passengers
							</p>

							<Table.Root class="mt-6">
								<Table.Header>
									<Table.Row>
										<Table.Head>0-6</Table.Head>
										<Table.Head>7-12</Table.Head>
										<Table.Head>13-18</Table.Head>
										<Table.Head>19-24</Table.Head>
									</Table.Row>
								</Table.Header>
								<Table.Body>
									<Table.Row>
										<Table.Cell>$7,653</Table.Cell>
										<Table.Cell>$8,391</Table.Cell>
										<Table.Cell>$9,130</Table.Cell>
										<Table.Cell>$9,867</Table.Cell>
									</Table.Row>
								</Table.Body>
							</Table.Root>
							<p class="mt-6 text-sm">
								<span class="underline">Please note:</span> Prices listed exclude meals, accommodation,
								entry and parks pass fees.
							</p>
							<Button
								href="/tours/private-tour-package-4-bruny-island-freycinet-national-park-cradle-mountain-launceston"
								data-sveltekit-noscroll
								class="pointer-events-auto mx-auto mt-6 w-44  rounded-full bg-tt-red px-6 text-lg font-semibold text-background  hover:bg-tt-red/85 lg:mx-0"
								>Book now</Button
							>
						</div>
					</div>

					<div>
						<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
							<enhanced:img
								src="$lib/assets/images/private-tours-1.jpg"
								alt="Hobart Highlights"
								class="h-full w-full object-cover object-center"
							/>
						</div>
						<div class="mt-4 grid grid-cols-2 gap-4 sm:mt-6 sm:gap-6 lg:mt-8 lg:gap-8">
							<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
								<enhanced:img
									src="$lib/assets/images/honeymoon.png"
									alt="Hobart Highlights"
									class="h-full w-full object-cover object-center"
								/>
							</div>
							<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
								<enhanced:img
									src="$lib/assets/images/cradle-3.jpg"
									alt="Hobart Highlights"
									class="h-full w-full object-cover object-center"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="">
			<div class="mx-auto max-w-2xl px-4 py-12 lg:max-w-7xl lg:py-24">
				<div class="grid grid-cols-1 items-center gap-x-8 gap-y-16 lg:grid-cols-2">
					<div class="">
						<div class="border-b border-gray-200 pb-10">
							<h2 class="font-medium">Private tour package 5</h2>
							<p class="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
								5 Days / 4 Nights
							</p>
							<div class="mt-4 flex items-center text-[#194139]">
								<MapPin class="mr-2 h-4 w-4 " />
								<p>Departs from Hobart. Finishes in Launceston. (Can be done in reverse)</p>
							</div>
						</div>

						<div class="mt-10">
							<p class="font-semibold leading-7 [&:not(:first-child)]:mt-6">
								This tour includes the following:
							</p>
							<ul class="my-6 [&>li]:mt-2">
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 1:</span> Hobart Highlights
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 2:</span> Bruny Island
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 3:</span> Freycinet National Park including Wineglass
										Bay
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 4:</span> St Helens & Bay of Fires
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 5:</span> Cradle Mountain
									</p>
								</li>
							</ul>
							<p class="font-semibold leading-7 [&:not(:first-child)]:mt-6">
								Prices based on the number of passengers
							</p>

							<Table.Root class="mt-6">
								<Table.Header>
									<Table.Row>
										<Table.Head>0-6</Table.Head>
										<Table.Head>7-12</Table.Head>
										<Table.Head>13-18</Table.Head>
										<Table.Head>19-24</Table.Head>
									</Table.Row>
								</Table.Header>
								<Table.Body>
									<Table.Row>
										<Table.Cell>$9,280</Table.Cell>
										<Table.Cell>$10,190</Table.Cell>
										<Table.Cell>$11,095</Table.Cell>
										<Table.Cell>$12,000</Table.Cell>
									</Table.Row>
								</Table.Body>
							</Table.Root>
							<p class="mt-6 text-sm">
								<span class="underline">Please note:</span> Prices listed exclude meals, accommodation,
								entry and parks pass fees.
							</p>
							<Button
								href="/tours/private-tour-package-5-hobart-bruny-island-freycinet-national-park-st-helens-and-bay-of-fire-cradle-mountain"
								data-sveltekit-noscroll
								class="pointer-events-auto mx-auto mt-6 w-44  rounded-full bg-tt-red px-6 text-lg font-semibold text-background  hover:bg-tt-red/85 lg:mx-0"
								>Book now</Button
							>
						</div>
					</div>

					<div>
						<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
							<enhanced:img
								src="$lib/assets/images/day-1.jpg"
								alt="Mount Field"
								class="h-full w-full object-cover object-center"
							/>
						</div>
						<div class="mt-4 grid grid-cols-2 gap-4 sm:mt-6 sm:gap-6 lg:mt-8 lg:gap-8">
							<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
								<enhanced:img
									src="$lib/assets/images/beach.jpg"
									alt="Mount Field"
									class="h-full w-full object-cover object-center"
								/>
							</div>
							<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
								<enhanced:img
									src="$lib/assets/images/raspberries.jpg"
									alt="Mount Field"
									class="h-full w-full object-cover object-center"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="">
			<div class="mx-auto max-w-2xl px-4 py-12 lg:max-w-7xl lg:py-24">
				<div class="grid grid-cols-1 items-center gap-x-8 gap-y-16 lg:grid-cols-2">
					<div class="lg:order-2">
						<div class="border-b border-gray-200 pb-10">
							<h2 class="font-medium">Private tour package 6</h2>
							<p class="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
								5 Days / 4 Nights
							</p>
							<div class="mt-4 flex items-center text-[#194139]">
								<MapPin class="mr-2 h-4 w-4 " />
								<p>Departs from Hobart. Finishes in Launceston. (Can be done in reverse)</p>
							</div>
						</div>

						<div class="mt-10">
							<p class="font-semibold leading-7 [&:not(:first-child)]:mt-6">
								This tour includes the following:
							</p>
							<ul class="my-6 [&>li]:mt-2">
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 1:</span> Hobart Highlights
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 2:</span> Bruny Island
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 3:</span> Mt Field National Park, Bonorong and Richmond
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 4:</span> Freycinet National Park including Wineglass
										Bay
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 5:</span> Cradle Mountain
									</p>
								</li>
							</ul>
							<p class="font-semibold leading-7 [&:not(:first-child)]:mt-6">
								Prices based on the number of passengers
							</p>

							<Table.Root class="mt-6">
								<Table.Header>
									<Table.Row>
										<Table.Head>0-6</Table.Head>
										<Table.Head>7-12</Table.Head>
										<Table.Head>13-18</Table.Head>
										<Table.Head>19-24</Table.Head>
									</Table.Row>
								</Table.Header>
								<Table.Body>
									<Table.Row>
										<Table.Cell>$9,302</Table.Cell>
										<Table.Cell>$10,210</Table.Cell>
										<Table.Cell>$11,100</Table.Cell>
										<Table.Cell>$12,000</Table.Cell>
									</Table.Row>
								</Table.Body>
							</Table.Root>
							<p class="mt-6 text-sm">
								<span class="underline">Please note:</span> Prices listed exclude meals, accommodation,
								entry and parks pass fees.
							</p>
							<Button
								href="/tours/private-tour-package-6-hobart-bruny-island-mt-field-national-park-freycinet-national-park-cradle-mountain"
								data-sveltekit-noscroll
								class="pointer-events-auto mx-auto mt-6 w-44  rounded-full bg-tt-red px-6 text-lg font-semibold text-background  hover:bg-tt-red/85 lg:mx-0"
								>Book now</Button
							>
						</div>
					</div>

					<div>
						<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
							<enhanced:img
								src="$lib/assets/images/cradle-2.jpg"
								alt="Cradle Mountain"
								class="h-full w-full object-cover object-center"
							/>
						</div>
						<div class="mt-4 grid grid-cols-2 gap-4 sm:mt-6 sm:gap-6 lg:mt-8 lg:gap-8">
							<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
								<enhanced:img
									src="$lib/assets/images/russell-falls.jpg"
									alt="Cradle Mountain"
									class="h-full w-full object-cover object-center"
								/>
							</div>
							<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
								<enhanced:img
									src="$lib/assets/images/hazards.jpg"
									alt="Cradle Mountain"
									class="h-full w-full object-cover object-center"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="">
			<div class="mx-auto max-w-2xl px-4 py-12 lg:max-w-7xl lg:py-24">
				<div class="grid grid-cols-1 items-center gap-x-8 gap-y-16 lg:grid-cols-2">
					<div class="">
						<div class="border-b border-gray-200 pb-10">
							<h2 class="font-medium">Private tour package 7</h2>
							<p class="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
								6 Days / 5 Nights
							</p>
							<div class="mt-4 flex items-center text-[#194139]">
								<MapPin class="mr-2 h-4 w-4 " />
								<p>Departs from Hobart. Finishes in Launceston. (Can be done in reverse)</p>
							</div>
						</div>

						<div class="mt-10">
							<p class="font-semibold leading-7 [&:not(:first-child)]:mt-6">
								This tour includes the following:
							</p>
							<ul class="my-6 [&>li]:mt-2">
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 1:</span> Hobart Highlights
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 2:</span> Bruny Island
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 3:</span> Port Arthur Bay
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 4:</span> Mt Field National Park, Bonorong and Richmond
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 5:</span> Freycinet National Park including Wineglass
										Bay
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 6:</span> Cradle Mountain
									</p>
								</li>
							</ul>
							<p class="font-semibold leading-7 [&:not(:first-child)]:mt-6">
								Prices based on the number of passengers
							</p>

							<Table.Root class="mt-6">
								<Table.Header>
									<Table.Row>
										<Table.Head>0-6</Table.Head>
										<Table.Head>7-12</Table.Head>
										<Table.Head>13-18</Table.Head>
										<Table.Head>19-24</Table.Head>
									</Table.Row>
								</Table.Header>
								<Table.Body>
									<Table.Row>
										<Table.Cell>$10,787</Table.Cell>
										<Table.Cell>$11,970</Table.Cell>
										<Table.Cell>$13,155</Table.Cell>
										<Table.Cell>$14,333</Table.Cell>
									</Table.Row>
								</Table.Body>
							</Table.Root>
							<p class="mt-6 text-sm">
								<span class="underline">Please note:</span> Prices listed exclude meals, accommodation,
								entry and parks pass fees.
							</p>
							<Button
								href="/tours/private-tour-package-7-hobart-bruny-island-port-arthur-mt-field-national-park-freycinet-national-park-cradle-mountain"
								data-sveltekit-noscroll
								class="pointer-events-auto mx-auto mt-6 w-44  rounded-full bg-tt-red px-6 text-lg font-semibold text-background  hover:bg-tt-red/85 lg:mx-0"
								>Book now</Button
							>
						</div>
					</div>

					<div>
						<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
							<enhanced:img
								src="$lib/assets/images/port-arthur-brochure.jpg"
								alt="Strahan"
								class="h-full w-full object-cover object-center"
							/>
						</div>
						<div class="mt-4 grid grid-cols-2 gap-4 sm:mt-6 sm:gap-6 lg:mt-8 lg:gap-8">
							<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
								<enhanced:img
									src="$lib/assets/images/roaming-tas.jpg"
									alt="Strahan"
									class="h-full w-full object-cover object-center"
								/>
							</div>
							<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
								<enhanced:img
									src="$lib/assets/images/wellington-walkway.jpg"
									alt="Strahan"
									class="h-full w-full object-cover object-center"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="">
			<div class="mx-auto max-w-2xl px-4 py-12 lg:max-w-7xl lg:py-24">
				<div class="grid grid-cols-1 items-center gap-x-8 gap-y-16 lg:grid-cols-2">
					<div class="lg:order-2">
						<div class="border-b border-gray-200 pb-10">
							<h2 class="font-medium">Private tour package 8</h2>
							<p class="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
								7 Days / 6 Nights
							</p>
							<div class="mt-4 flex items-center text-[#194139]">
								<MapPin class="mr-2 h-4 w-4 " />
								<p>Departs from Hobart. Finishes in Launceston. (Can be done in reverse)</p>
							</div>
						</div>

						<div class="mt-10">
							<p class="font-semibold leading-7 [&:not(:first-child)]:mt-6">
								This tour includes the following:
							</p>
							<ul class="my-6 [&>li]:mt-2">
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 1:</span> Hobart Highlights
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 2:</span> Bruny Island
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 3:</span> Port Arthur
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 4:</span> Mt Field National Park, Bonorong and Richmond
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 5:</span> Freycinet National Park including Wineglass
										Bay
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 6:</span> Cradle Mountain (2 night stay)
									</p>
								</li>
								<li class="flex items-center">
									<Check class="mr-2 h-5 w-5 text-tt-red" />
									<p class=" leading-7">
										<span class="font-semibold">Day 7:</span> Cradle Mountain - Launceston
									</p>
								</li>
							</ul>
							<p class="font-semibold leading-7 [&:not(:first-child)]:mt-6">
								Prices based on the number of passengers
							</p>

							<Table.Root class="mt-6">
								<Table.Header>
									<Table.Row>
										<Table.Head>0-6</Table.Head>
										<Table.Head>7-12</Table.Head>
										<Table.Head>13-18</Table.Head>
										<Table.Head>19-24</Table.Head>
									</Table.Row>
								</Table.Header>
								<Table.Body>
									<Table.Row>
										<Table.Cell>$12,813</Table.Cell>
										<Table.Cell>$14,653</Table.Cell>
										<Table.Cell>$16,600</Table.Cell>
										<Table.Cell>$16,600</Table.Cell>
									</Table.Row>
								</Table.Body>
							</Table.Root>
							<p class="mt-6 text-sm">
								<span class="underline">Please note:</span> Prices listed exclude meals, accommodation,
								entry and parks pass fees.
							</p>
							<Button
								href="/tours/private-tour-package-7-hobart-bruny-island-port-arthur-mt-field-national-park-freycinet-national-park-cradle-mountain"
								data-sveltekit-noscroll
								class="pointer-events-auto mx-auto mt-6 w-44  rounded-full bg-tt-red px-6 text-lg font-semibold text-background  hover:bg-tt-red/85 lg:mx-0"
								>Book now</Button
							>
						</div>
					</div>

					<div>
						<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
							<enhanced:img
								src="$lib/assets/images/tas-devil.png"
								alt="Cradle Mountain"
								class="h-full w-full object-cover object-center"
							/>
						</div>
						<div class="mt-4 grid grid-cols-2 gap-4 sm:mt-6 sm:gap-6 lg:mt-8 lg:gap-8">
							<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
								<enhanced:img
									src="$lib/assets/images/cascade-beer.jpeg"
									alt="Cradle Mountain"
									class="h-full w-full object-cover object-center"
								/>
							</div>
							<div class="aspect-h-1 aspect-w-1 overflow-hidden rounded-xl bg-gray-100">
								<enhanced:img
									src="$lib/assets/images/hero-carousel-1.jpg"
									alt="Cradle Mountain"
									class="h-full w-full object-cover object-center"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</section>

<section class="mx-auto px-4 pb-12" id="contact">
	<div class=" mx-auto max-w-7xl">
		<div class="mx-auto lg:mx-0">
			<h2
				class="text-center font-heading text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl"
			>
				Book your private tour today!
			</h2>
			<div class="mx-auto mt-6 flex max-w-3xl">
				<form
					onsubmit={handleSubmit}
					data-sveltekit-preload-data="off"
					class=" relative flex w-full flex-col items-center justify-center gap-10 px-4 pb-12 pt-6"
				>
					<p class="max-w-lg text-center">
						For a <strong>FREE</strong> Quote or any further enquiries, please contact us, your experts
						in fun Tasmania tours
					</p>
					<div class=" flex flex-wrap items-center justify-center">
						<Button variant="link" href="tel:0409404337" target="_blank" on:click={trackPhoneClick}>0409 404 337</Button> • <Button
							variant="link"
							href="mailto:info@tassietours.com"
							target="_blank">info@tassietours.com</Button
						> •
						<Button variant="link" href="mailto:info@tassietours.com" target="_blank"
							>7am to 7pm</Button
						>
					</div>
					<div class="flex w-full flex-col gap-1.5">
						<Label for="name">Name</Label>
						<Input required type="text" id="name" placeholder="Name" bind:value={name} />
					</div>
					<div class="flex w-full gap-4">
						<div class="flex w-1/2 flex-col gap-1.5">
							<Label for="email">Email</Label>
							<Input
								required
								type="email"
								id="email"
								placeholder="email@domain.com"
								bind:value={email}
							/>
						</div>
						<div class="flex w-1/2 flex-col gap-1.5">
							<Label for="phone">Phone</Label>
							<Input
								required
								type="phone"
								id="phone"
								placeholder="Phone number"
								bind:value={phone}
							/>
						</div>
					</div>

					<div class="flex w-full flex-col gap-1.5">
						<Label for="preferredContact">Preferred contact method</Label>
						<Select.Root type="single" bind:value={preferredContact}>
							<Select.Trigger class="">
								{triggerContent}
							</Select.Trigger>
							<Select.Content>
								<Select.Group>
									{#each contactMethods as method}
										<Select.Item value={method.value} label={method.label}
											>{method.label}</Select.Item
										>
									{/each}
								</Select.Group>
							</Select.Content>
						</Select.Root>
					</div>
					<div class="flex w-full flex-col gap-1.5">
						<Label for="message-2">Message</Label>
						<Textarea
							required
							placeholder="Type your message here."
							id="message-2"
							bind:value={message}
						/>
						<p class="text-sm text-muted-foreground">
							Let us know how we can make your Tasmanian experience even better.
						</p>
					</div>
					<div class="relative mt-6 flex h-32 w-full items-center justify-center">
						{#if !submitted}
							<div
								class="absolute inset-0 flex justify-center"
								in:fly={{ x: -50, duration: 300 }}
								out:fly={{ x: -50, duration: 300 }}
							>
								{#if !loading}
									<Button
										type="submit"
										class="z-50 min-w-32 overflow-hidden rounded-full bg-tt-red p-3 text-center font-semibold text-background transition-all hover:bg-tt-red/80"
									>
										Submit
									</Button>
								{:else}
									<Button
										variant="outline"
										disabled
										class="z-50 flex min-w-32 overflow-hidden rounded-full bg-tt-red p-3 text-center font-semibold text-background transition-all"
									>
										<Loader2 class="mr-2 h-4 w-4 animate-spin" />
									</Button>
								{/if}
							</div>
						{:else}
							<div
								class="absolute inset-0 mx-auto flex w-[500px] items-center justify-center"
								in:fly={{ x: 50, duration: 300, delay: 300 }}
								out:fly={{ x: -50, duration: 300 }}
							>
								<Alert.Root onclick={() => (submitted = false)} class="text-left hover:bg-accent">
									<CheckCircle class="h-4 w-4" />
									<Alert.Title>Your submission has been received!</Alert.Title>
									<Alert.Description class="text-wrap">
										Keep an eye out. A member of our team will get back to you within 3 business
										days.
									</Alert.Description>
								</Alert.Root>
							</div>
						{/if}
					</div>
				</form>
			</div>
		</div>
	</div>
</section>
